import { isProjectFolder } from '@papan/types'
import { filesApi } from '../client'
import { requestOpensAsProject } from '../client/queries'

/**
 * Does this project / app folder browse as the PLAIN folder the drive shows, rather than open as
 * a project or an app? The ONE check desktop and mobile open paths share.
 *
 * - A PROJECT (`.project`) opens as one only where the caller can read its workspace config (#1406,
 *   owner 2026-10-26 — the server's `projectOpenChecker`, asked through
 *   `GET /api/workspaces/opens-as-project/:id`, so the drive and every project route give one answer).
 *   A hallway project, or one whose `.config/` they can't read, is a plain folder to them.
 * - An APP is reached only as a HALLWAY — No access on it, something below it granted (#1036): the
 *   app launcher needs read on the folder. A hallway row under a hidden parent carries no level (the
 *   listing leaves it undefined), so ask `/permission` once. Opening an app AS A WORKSPACE (Edit PDApp,
 *   `asProject`) is opening it as a project, so that asks the project rule too.
 */
export async function packageIsHallwayShell(
  file: { id: string; name?: string; isFolder?: boolean; effectivePermission?: string },
  opts: { asProject?: boolean } = {},
): Promise<boolean> {
  if (!file.isFolder) return false
  if (opts.asProject || (file.name && isProjectFolder(file.name))) {
    const opens = await requestOpensAsProject(file.id)
      .catch((e: { status?: number } | null) => (e?.status === 404 ? undefined : 'failed' as const))
    // A check that failed (a 429, a 5xx, the network) can't say it opens: the plain folder — never a project
    // page the server may refuse (the package crumb then hides "Open project" for that visit: fail safe).
    // Only an older server (mid-deploy, no such route: a 404) falls back to the folder's own level.
    if (opens === 'failed') return true
    if (typeof opens === 'boolean') return !opens
  }
  if (file.effectivePermission !== undefined) return file.effectivePermission === 'hidden'
  return (await filesApi.getPermission(file.id).catch(() => null)) === 'hidden'
}
